import { BUDGET_RESET_PERIODS, MAX_BUDGET_HOURS } from '@clockwise/shared';
import { z } from 'zod';

/**
 * Per-role allocation map on a budget: `{ "<serviceRoleId>": hours }`.
 *
 * Keyed by role **id**, never by name — a rename must not orphan the budget.
 * A role with no allowance is ABSENT: there is no zero entry, because
 * "unbudgeted" and "budgeted at zero" are different answers and only one of
 * them can be exceeded.
 */
export const BudgetAllocationsInput = z
  .record(
    z.string().uuid(),
    z
      .number()
      .positive()
      .max(MAX_BUDGET_HOURS)
      .refine((n) => Number(n.toFixed(2)) === n, 'at most 2 decimal places'),
  )
  .refine((a) => Object.keys(a).length > 0, 'at least one service role must have budgeted hours');

export interface CustomerBudget {
  id: string;
  tenantId: string;
  customerId: string;
  resetPeriod: (typeof BUDGET_RESET_PERIODS)[number];
  allocations: Record<string, number>;
  /** The engagement this budget funds — one of its own customer's. Required
   *  on every write; nullable on the ROW only because budgets predating
   *  engagements still carry null until they are cleaned up, and such a budget
   *  funds nothing. */
  engagementId: string | null;
  /** Server-resolved display name for `engagementId` (its SOW's latest
   *  version), so readers need neither a second request nor
   *  `customer.sow.view`. Null on a legacy unattached budget — or when the id
   *  no longer resolves, which the UI reports as a gap rather than inventing a
   *  name. */
  engagementName: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  createdAt: string;
  updatedAt: string;
}

export const CreateCustomerBudgetInput = z
  .object({
    resetPeriod: z.enum(BUDGET_RESET_PERIODS),
    allocations: BudgetAllocationsInput,
    /** REQUIRED — hours are measured against the pool for their own
     *  engagement, so a budget attached to none can never be consumed. */
    engagementId: z.string().uuid(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable().optional(),
  })
  .strict();
export type CreateCustomerBudgetInput = z.infer<typeof CreateCustomerBudgetInput>;

// `allocations` is replaced WHOLESALE when present — there is no per-role
// merge, because a budget is one agreement and a partial map has no good
// answer to "what happened to the role you left out?". Omit the key to leave
// the stored map alone.
export const PatchCustomerBudgetInput = z
  .object({
    resetPeriod: z.enum(BUDGET_RESET_PERIODS).optional(),
    allocations: BudgetAllocationsInput.optional(),
    /** Re-attach to a different engagement; omit to leave it alone. There is
     *  no null — a budget cannot be detached. */
    engagementId: z.string().uuid().optional(),
    effectiveFrom: z.string().optional(),
    effectiveTo: z.string().nullable().optional(),
  })
  .strict();
export type PatchCustomerBudgetInput = z.infer<typeof PatchCustomerBudgetInput>;

/**
 * One row of the budget import report, in upload order.
 *
 * `unchanged` is a first-class outcome here, not a curiosity: an import row
 * that matches the stored budget exactly is ignored, and a re-import of an
 * unedited export is therefore all-unchanged. `updated` never occurs —
 * budgets are versioned, not edited in place, so a changed row produces a
 * NEW budget (`created`) and reshapes the ones it overlaps.
 */
export type ImportRowOutcome = 'created' | 'unchanged' | 'failed';

export interface ImportBudgetRow {
  rowNumber: number;
  /** Client ID (qbId) of the owning customer, as it appeared in the file. */
  qbId: string | null;
  /** Customer name for the report, when the id resolved. */
  customerName: string | null;
  outcome: ImportRowOutcome;
  errors?: string[];
}

export interface ImportBudgetResult {
  totalRows: number;
  created: number;
  /** Always 0 — budgets are versioned rather than updated. Present so the
   *  shared `ImportSheet` renderer can treat every importer alike. */
  updated: number;
  unchanged: number;
  failed: number;
  rows: ImportBudgetRow[];
}

/** One role's budget-versus-actual within one period occurrence. */
export interface BudgetPeriodRoleActual {
  /** The budget's own allowance for the role, granted WHOLE — never prorated,
   *  even when the budget covers only part of the period.
   *
   *  Taken from the REPORT, not from the budget row: where two consecutive
   *  budgets describe one period, `budget-actuals.sql` picks the one that
   *  governed most of it, and this tile shows what that report shows. */
  budgetHours: number;
  actualHours: number;
  /** `budgetHours - actualHours`: positive is headroom, negative is overage. */
  varianceHours: number;
  /** No pool covers this (engagement, role, period): the allowance shown is
   *  the budget row's own and nothing has been measured against it. Distinct
   *  from "measured, and the answer was zero" — a blank actual reads as the
   *  first, which is why this is on the wire rather than inferred. */
  unmeasured: boolean;
}

/** One period occurrence of a budget, with its actuals. */
export interface BudgetPeriodActuals {
  /** Half-open `[periodStart, periodEnd)`, anchored to the calendar year. */
  periodStart: string;
  periodEnd: string;
  /** `Jul 2026` / `Q3 2026` / `H1 2026` / `2026` — the budget's own reset
   *  period, so a quarterly budget reports quarters. */
  label: string;
  /** The window hours were measured over: the period intersected with the
   *  budget's effective range. */
  measuredFrom: string;
  measuredTo: string;
  /** The budget's effective range doesn't cover the whole period. */
  partial: boolean;
  /** The period hasn't finished AND the budget is still running, so its
   *  actuals are still accruing. A budget that ended mid-period is NOT in
   *  progress even though its calendar period is still open — don't derive
   *  this from `periodEnd` alone, the two disagree in exactly that case. */
  inProgress: boolean;
  /** Keyed by service-role id, covering exactly the budget's allocated roles. */
  roles: Record<string, BudgetPeriodRoleActual>;
  totals: { budgetHours: number; actualHours: number; varianceHours: number };
}

export interface CustomerBudgetActuals {
  budgetId: string;
  /** Newest period first; fewer than requested when the budget doesn't reach
   *  that far back. */
  periods: BudgetPeriodActuals[];
}

/** One choice in the budget form's engagement picker. Names only — enough to
 *  pick one, and nothing a SOW read would additionally reveal. */
export interface BudgetEngagementOption {
  id: string;
  name: string;
  sowId: string;
  sowName: string;
}
